/**
 * Extraccion segura del mensaje de error de una respuesta de la API.
 *
 * Por que existe: el backend envuelve los errores de validacion como
 * `{ code: 4000, message: { <campo>: ["<error de DRF>"] } }`. El patron
 * `error?.response?.data?.message || "fallback"`, repetido por toda la app,
 * deja pasar ESE OBJETO tal cual. Si el objeto acaba como hijo de React
 * (un toast de sonner, la `description` de un Alert de antd) React 19 lanza
 * "Objects are not valid as a React child" y, como el `<Toaster/>` vive fuera
 * del ErrorBoundary, se cae la aplicacion entera (Sentry MB-APP-EXPRESS-1P).
 *
 * Contrato de este modulo:
 *  - `getApiErrorMessage` SIEMPRE devuelve un string no vacio y NUNCA lanza.
 *  - Nunca devuelve "[object Object]", ni JSON crudo, ni un escalar
 *    convertido a texto (el `code: 4000` del envelope no es un mensaje).
 *  - Nunca usa `String(value)` ni `toString()` sobre el payload.
 *  - No llama a `Sentry.captureException`/`captureMessage`: eso abriria un
 *    issue nuevo y recurrente en Sentry. Como maximo deja una migaja.
 */
import * as Sentry from "@sentry/react";

/** Texto de ultimo recurso cuando no hay NADA utilizable en la respuesta. */
export const DEFAULT_API_ERROR_MESSAGE = "Algo salió mal, inténtalo de nuevo.";

/** Lo que se dice de un campo cuyo error de DRF no sabemos traducir. */
const UNKNOWN_FIELD_MESSAGE = "revisa este dato.";

const MAX_FIELDS = 3;
const MAX_DEPTH = 3;
const MAX_LIST_ITEMS = 3;
const MAX_LENGTH = 240;
const SEPARATOR = " · ";

/** Etiquetas en espanol para las claves que manda el serializer. */
export const FIELD_LABELS: Record<string, string> = {
  prospectus_email: "Correo del prospecto",
  legal_type: "Tipo de persona",
  report_types: "Tipo de reporte",
  email: "Correo",
  phone_number: "Teléfono",
  dial_code: "Lada",
  password: "Contraseña",
  new_password: "Contraseña nueva",
  name: "Nombre",
  first_surname: "Apellido paterno",
  second_surname: "Apellido materno",
  rfc: "RFC",
  curp: "CURP",
  birth_date: "Fecha de nacimiento",
  zip_code: "Código postal",
  monthly_rent: "Renta mensual",
  start_date: "Fecha de inicio",
  end_date: "Fecha de fin",
};

/**
 * Claves que NO nombran un campo: su contenido se muestra sin etiqueta.
 * `non_field_errors` y `detail` son de DRF; `message`/`error` son del envelope.
 */
const LABELLESS_KEYS = new Set([
  "non_field_errors",
  "detail",
  "details",
  "message",
  "error",
  "errors",
  "__all__",
]);

/**
 * Claves del envelope que hay que ignorar cuando se recorre `data` como si
 * fuera un diccionario de campos (ya se intentaron antes, o no son mensajes).
 */
const ENVELOPE_KEYS = new Set([
  "code",
  "message",
  "detail",
  "status",
  "status_code",
  "statusCode",
  "success",
  "ok",
  "data",
]);

/** Traducciones exactas de los mensajes estandar de DRF. */
export const DRF_MESSAGES: Record<string, string> = {
  "This field may not be blank.": "Es obligatorio.",
  "This field may not be null.": "Es obligatorio.",
  "This field is required.": "Es obligatorio.",
  "No file was submitted.": "Falta el archivo.",
  "Enter a valid email address.": "Ingresa un correo válido.",
  "Enter a valid URL.": "Ingresa una URL válida.",
  "Enter a valid date.": "Ingresa una fecha válida.",
  "Enter a valid date/time.": "Ingresa una fecha y hora válidas.",
  "A valid integer is required.": "Ingresa un número válido.",
  "A valid number is required.": "Ingresa un número válido.",
  "Must be a valid boolean.": "El valor no es válido.",
  "Not found.": "No se encontró.",
  "Invalid page.": "La página no es válida.",
  "Incorrect type. Expected pk value, received str.": "El valor no es válido.",
  "This field must be unique.": "Ya existe un registro con este dato.",
  "Authentication credentials were not provided.":
    "Tu sesión expiró, inicia sesión de nuevo.",
  "Incorrect authentication credentials.": "Tus credenciales no son correctas.",
  "You do not have permission to perform this action.":
    "No tienes permiso para hacer esto.",
};

/** Mensajes de DRF que traen un valor dentro y necesitan patron. */
const DRF_PATTERNS: Array<[RegExp, (match: RegExpMatchArray) => string]> = [
  [
    /^Ensure this field has no more than (\d+) characters\.$/,
    (m) => `No debe tener más de ${m[1]} caracteres.`,
  ],
  [
    /^Ensure this field has at least (\d+) characters\.$/,
    (m) => `Debe tener al menos ${m[1]} caracteres.`,
  ],
  [
    /^Ensure this value is less than or equal to (.+)\.$/,
    (m) => `Debe ser menor o igual a ${m[1]}.`,
  ],
  [
    /^Ensure this value is greater than or equal to (.+)\.$/,
    (m) => `Debe ser mayor o igual a ${m[1]}.`,
  ],
  [/is not a valid choice\.$/, () => "El valor seleccionado no es válido."],
];

/**
 * Palabras que solo existen en ingles (nada de "no", "error", "a" o "la",
 * que son ambiguas). Sirven para no filtrar ingles crudo a un usuario es-MX.
 */
const ENGLISH_MARKERS = new Set([
  "the",
  "this",
  "that",
  "these",
  "those",
  "field",
  "fields",
  "must",
  "may",
  "not",
  "cannot",
  "can",
  "does",
  "do",
  "did",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "has",
  "have",
  "had",
  "with",
  "without",
  "value",
  "values",
  "already",
  "exists",
  "exist",
  "found",
  "blank",
  "null",
  "characters",
  "character",
  "enter",
  "ensure",
  "invalid",
  "required",
  "valid",
  "please",
  "try",
  "again",
  "unable",
  "expected",
  "received",
  "and",
  "or",
  "of",
  "for",
  "at",
  "to",
  "in",
  "on",
  "an",
  "it",
  "you",
  "your",
  "we",
  "request",
  "failed",
  "status",
  "only",
  "allowed",
  "supported",
  "choice",
  "submitted",
  "provided",
  "permission",
  "perform",
  "action",
  "credentials",
  "unique",
  "page",
]);

/** Acentos y signos que garantizan que el texto NO es ingles. */
const SPANISH_CHARS = /[áéíóúüñÁÉÍÓÚÜÑ¿¡]/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Un elemento de React (`<b/>`) es un hijo valido y NO debe coercionarse:
 * convertirlo a texto generico seria cambiar un toast correcto por otro peor.
 */
const isReactElement = (value: unknown): boolean =>
  isRecord(value) &&
  typeof (value as { $$typeof?: unknown }).$$typeof === "symbol";

/** Lee una propiedad sin que un getter que lanza tumbe la extraccion. */
const readProp = (source: unknown, key: string): unknown => {
  try {
    return (source as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
};

const isUntranslatedEnglish = (text: string): boolean => {
  if (SPANISH_CHARS.test(text)) return false;
  const words = text
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(Boolean);
  let markers = 0;
  for (const word of words) {
    if (ENGLISH_MARKERS.has(word)) markers += 1;
    if (markers >= 2) return true;
  }
  return false;
};

/**
 * Convierte UN mensaje del backend en texto mostrable.
 * `translated` es el texto en espanol si lo sabemos traducir (o si ya venia en
 * espanol); `raw` es siempre el original recortado.
 */
const normalizeMessage = (
  value: string,
): { raw: string; translated: string | null } | null => {
  const raw = value.trim();
  if (!raw) return null;
  // Un cuerpo HTML (una pagina de error de nginx, por ejemplo) no es un mensaje.
  if (raw.startsWith("<") || raw.startsWith("{") || raw.startsWith("[")) {
    return null;
  }

  const exact = DRF_MESSAGES[raw];
  if (exact) return { raw, translated: exact };

  for (const [pattern, build] of DRF_PATTERNS) {
    const match = raw.match(pattern);
    if (match) return { raw, translated: build(match) };
  }

  if (isUntranslatedEnglish(raw)) return { raw, translated: null };
  return { raw, translated: raw };
};

const humanizeKey = (key: string): string => {
  const words = key.replace(/[_-]+/g, " ").trim().toLowerCase();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const labelFor = (key: string | null): string | null => {
  if (!key || LABELLESS_KEYS.has(key)) return null;
  const known = FIELD_LABELS[key];
  if (known) return known;
  const humanized = humanizeKey(key);
  return humanized || null;
};

/**
 * Une los mensajes de un campo. Si ninguno se puede traducir y el campo TIENE
 * etiqueta, se dice el nombre del campo con un texto generico en espanol: el
 * usuario sigue sabiendo QUE dato revisar, sin leer ingles. Si no hay etiqueta
 * (`detail`, `message`, `non_field_errors`) se muestra el texto original, que
 * es el comportamiento que ya tenia la app para esos casos.
 */
const composeFieldText = (
  key: string | null,
  rawMessages: string[],
): string | null => {
  const parsed = rawMessages
    .map(normalizeMessage)
    .filter((entry): entry is { raw: string; translated: string | null } =>
      Boolean(entry),
    );
  if (parsed.length === 0) return null;

  const label = labelFor(key);
  const translated = parsed
    .map((entry) => entry.translated)
    .filter((text): text is string => Boolean(text))
    .slice(0, 2);

  if (translated.length > 0) {
    const body = translated.join(" ");
    return label ? `${label}: ${body}` : body;
  }

  if (label) return `${label}: ${UNKNOWN_FIELD_MESSAGE}`;
  return parsed[0].raw;
};

const flattenFieldErrors = (
  dict: Record<string, unknown>,
  depth: number,
  seen: WeakSet<object>,
  skipKeys?: Set<string>,
): string | null => {
  if (seen.has(dict)) return null;
  seen.add(dict);

  const parts: string[] = [];
  for (const key of Object.keys(dict)) {
    if (parts.length >= MAX_FIELDS) break;
    if (skipKeys?.has(key)) continue;
    const text = describeEntry(key, readProp(dict, key), depth, seen);
    if (text && !parts.includes(text)) parts.push(text);
  }
  return parts.length > 0 ? parts.join(SEPARATOR) : null;
};

/**
 * Describe el valor de UNA clave del diccionario de errores.
 * Para un diccionario anidado gana la etiqueta mas interna, que es la que
 * nombra el campo de verdad (`{guarantor: {email: [...]}}` -> "Correo: ...").
 */
function describeEntry(
  key: string | null,
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
): string | null {
  if (depth >= MAX_DEPTH) return null;

  if (isRecord(value)) {
    return flattenFieldErrors(value, depth + 1, seen);
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) return null;
    seen.add(value);
    const items = value.slice(0, MAX_LIST_ITEMS);
    const strings = items.filter(
      (item): item is string => typeof item === "string",
    );
    if (strings.length > 0) return composeFieldText(key, strings);
    for (const item of items) {
      if (isRecord(item)) {
        const nested = flattenFieldErrors(item, depth + 1, seen);
        if (nested) return nested;
      }
    }
    return null;
  }

  if (typeof value === "string") return composeFieldText(key, [value]);

  // Numeros, booleanos, null, funciones: el `code: 4000` del envelope NO es
  // un mensaje y convertirlo a texto le mostraria "4000" al usuario.
  return null;
}

/** Ultima red: garantiza que lo que se muestra es texto de verdad. */
const sanitizeForUser = (value: string | null | undefined): string | null => {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return null;
  if (text.includes("[object ")) return null;
  if (text.startsWith("{") || text.startsWith("[") || text.startsWith("<")) {
    return null;
  }
  // Proxy de JSON crudo: `{"campo":["..."]}` o `'campo": ...'`.
  //
  // ANCLADO a proposito: `text.includes('":')` tambien mataba prosa legitima en
  // espanol que cita un campo entre comillas — `Falta el campo "nombre":
  // revisalo` — y el usuario se quedaba con el mensaje generico en vez del
  // accionable. Ahora se exige que tras el `:` venga un ABRE-valor de JSON
  // (`"`, `[` o `{`), que es lo que distingue un fragmento serializado de una
  // frase. `{"campo":["x"]}` y `campo":["x"]` siguen cayendo; la frase no.
  if (/"\s*:\s*["[{]/.test(text)) return null;
  if (/\b(undefined|NaN)\b/.test(text)) return null;
  if (text.length <= MAX_LENGTH) return text;
  return `${text.slice(0, MAX_LENGTH - 1).trimEnd()}…`;
};

const OFFLINE_MESSAGE = "No pudimos conectarnos, revisa tu conexión.";
const TIMEOUT_MESSAGE = "La solicitud tardó demasiado, inténtalo de nuevo.";

/**
 * Codigos de axios que significan "NO hubo respuesta del servidor".
 * `null` = no hay nada util que decirle al usuario (que use su fallback).
 */
const TRANSPORT_MESSAGES: Record<string, string | null> = {
  ERR_NETWORK: OFFLINE_MESSAGE,
  ERR_INTERNET_DISCONNECTED: OFFLINE_MESSAGE,
  ECONNREFUSED: OFFLINE_MESSAGE,
  ECONNRESET: OFFLINE_MESSAGE,
  ENOTFOUND: OFFLINE_MESSAGE,
  ECONNABORTED: TIMEOUT_MESSAGE,
  ETIMEDOUT: TIMEOUT_MESSAGE,
  ERR_CANCELED: null,
};

/**
 * Distingue un ERROR (axios, `Error` de JS, `SerializedError` de redux) de un
 * PAYLOAD con forma de cuerpo de respuesta (`rejectWithValue({...})`, el dict
 * de DRF suelto). Importa porque en un Error la clave `message` es texto
 * tecnico que NO hay que mostrar, mientras que en un payload es el mensaje.
 */
const isErrorLike = (value: Record<string, unknown>): boolean => {
  if (value instanceof Error) return true;
  if (readProp(value, "isAxiosError") === true) return true;
  return (
    typeof readProp(value, "stack") === "string" &&
    typeof readProp(value, "message") === "string"
  );
};

/**
 * `error.message` de axios/Error, descartando el boilerplate inutil.
 *
 * Devuelve SOLO texto que el usuario puede leer: si el mensaje esta en ingles
 * y no lo sabemos traducir se devuelve `null` para que el call site use su
 * fallback en espanol. Nunca se devuelve el `raw` sin traducir: `error.message`
 * de un Error de JS o de axios es texto tecnico ("Network Error",
 * "Unexpected token < in JSON at position 0"), no un mensaje de producto.
 */
const fromErrorMessage = (message: string): string | null => {
  const text = message.trim();
  if (!text) return null;
  if (/^Request failed with status code \d+$/.test(text)) return null;
  if (/^timeout of \d+ms exceeded$/.test(text)) {
    return TIMEOUT_MESSAGE;
  }
  if (text === "Network Error") {
    return OFFLINE_MESSAGE;
  }
  const normalized = normalizeMessage(text);
  if (!normalized) return null;
  return normalized.translated;
};

/** Recorre un cuerpo de respuesta (o cualquier payload con esa forma). */
const fromDataLike = (data: unknown, seen: WeakSet<object>): string | null => {
  if (typeof data === "string") {
    const normalized = normalizeMessage(data);
    return normalized ? (normalized.translated ?? normalized.raw) : null;
  }
  if (Array.isArray(data)) return describeEntry(null, data, 0, seen);
  if (!isRecord(data)) return null;

  for (const key of ["message", "detail"] as const) {
    const value = readProp(data, key);
    if (typeof value === "string") {
      const text = describeEntry(key, value, 0, seen);
      if (text) return text;
    } else if (isRecord(value) || Array.isArray(value)) {
      // El diccionario del serializer viene AQUI: se recorre sin etiquetar la
      // clave del envelope, para que se vea la etiqueta del campo real.
      const text = describeEntry(null, value, 0, seen);
      if (text) return text;
    }
  }

  return flattenFieldErrors(data, 0, seen, ENVELOPE_KEYS);
};

const extractCandidate = (error: unknown): string | null => {
  if (typeof error === "string") {
    const normalized = normalizeMessage(error);
    return normalized ? (normalized.translated ?? normalized.raw) : null;
  }
  if (!isRecord(error)) return null;

  const seen = new WeakSet<object>();
  const response = readProp(error, "response");
  const hasResponse = isRecord(response);
  const data = hasResponse ? readProp(response, "data") : undefined;

  if (data !== undefined && data !== null) {
    const fromData = fromDataLike(data, seen);
    if (fromData) return fromData;
  }

  const message = readProp(error, "message");

  // ORDEN CRITICO: este bloque va ANTES de `fromDataLike(error, ...)`.
  // Sin `response` no hay cuerpo del servidor que leer: es un fallo de
  // TRANSPORTE (red caida, timeout, peticion cancelada) o un `Error` que
  // escapo de la mutationFn. Si se dejara pasar por `fromDataLike`, este
  // leeria `error.message` del propio Error y mostraria "Network Error" o
  // "timeout of 10000ms exceeded" tal cual: texto tecnico en ingles donde
  // antes se veia el fallback en espanol.
  if (!hasResponse && isErrorLike(error)) {
    const code = readProp(error, "code");
    if (typeof code === "string" && code in TRANSPORT_MESSAGES) {
      return TRANSPORT_MESSAGES[code];
    }
    // Un Error que lleva el diccionario del serializer encima (en vez de un
    // texto) si se puede describir: un objeto no es texto tecnico.
    if (isRecord(message) || Array.isArray(message)) {
      const fromDict = describeEntry(null, message, 0, seen);
      if (fromDict) return fromDict;
    }
    return typeof message === "string" ? fromErrorMessage(message) : null;
  }

  // Sin `response` el objeto puede SER el payload: un `rejectWithValue(...)`
  // de Redux o el diccionario de DRF suelto.
  if (!hasResponse) {
    const fromSelf = fromDataLike(error, seen);
    if (fromSelf) return fromSelf;
  }

  if (typeof message === "string") return fromErrorMessage(message);
  return null;
};

/**
 * Devuelve el mensaje de error listo para mostrarle al usuario.
 * Nunca lanza y nunca devuelve una cadena vacia.
 */
export function getApiErrorMessage(error: unknown, fallback?: string): string {
  const safeFallback = sanitizeForUser(fallback) ?? DEFAULT_API_ERROR_MESSAGE;
  try {
    return sanitizeForUser(extractCandidate(error)) ?? safeFallback;
  } catch {
    return safeFallback;
  }
}

/**
 * `getApiErrorMessage` para las descargas (`responseType: "blob"`): ahi el cuerpo
 * de un error tambien llega como Blob, y sin leerlo su `message` no se ve (el
 * usuario solo veia el fallback, p. ej. sin saber que falta la persona
 * facilitadora del convenio, MB-656). Si el Blob es JSON se lee y se extrae como
 * cualquier otra respuesta; si no, se trata como una respuesta sin cuerpo.
 * Igual que `getApiErrorMessage`: nunca lanza y nunca devuelve una cadena vacia.
 */
export async function getBlobApiErrorMessage(
  error: unknown,
  fallback?: string,
): Promise<string> {
  try {
    const response = isRecord(error) ? readProp(error, "response") : undefined;
    const data = isRecord(response) ? readProp(response, "data") : undefined;
    if (!isRecord(response) || !(data instanceof Blob)) {
      return getApiErrorMessage(error, fallback);
    }

    let body: unknown;
    try {
      body = data.type.includes("json")
        ? JSON.parse(await data.text())
        : undefined;
    } catch {
      // Ilegible o JSON roto: como si no hubiera cuerpo.
      body = undefined;
    }
    // El Blob mismo no es un cuerpo que se pueda recorrer: va el JSON leido o nada.
    return getApiErrorMessage(
      { response: { ...response, data: body } },
      fallback,
    );
  } catch {
    return getApiErrorMessage(undefined, fallback);
  }
}

/** Migaja (NO captura: una captura abriria un issue nuevo en Sentry). */
const breadcrumbNonTextToast = (value: unknown): void => {
  try {
    Sentry.addBreadcrumb({
      category: "ui.toast",
      level: "warning",
      message: "toast recibió un valor no textual",
      // Solo la forma del dato. Los cuerpos de estos endpoints traen correos
      // de prospectos: el contenido seria PII en el breadcrumb.
      data: {
        valueType: Array.isArray(value) ? "array" : typeof value,
        keys: isRecord(value) ? Object.keys(value).slice(0, 5).join(",") : "",
      },
    });
  } catch {
    // Sentry sin inicializar (tests, SSR): la migaja es best-effort.
  }
};

/**
 * Coercion defensiva para el contenido de un toast.
 *
 * Es la red de seguridad central: mientras el `<Toaster/>` viva fuera del
 * ErrorBoundary, CUALQUIER call site que pase un objeto tumba la app. Aqui se
 * convierte en un mensaje util (o, en el peor caso, en el fallback), nunca en
 * "[object Object]" ni en JSON.
 *
 * Los elementos de React pasan intactos: son hijos validos.
 */
export function coerceToastMessage(value: unknown, fallback?: string): unknown {
  if (typeof value === "string") {
    return (
      sanitizeForUser(value) ??
      sanitizeForUser(fallback) ??
      DEFAULT_API_ERROR_MESSAGE
    );
  }
  if (isReactElement(value)) return value;
  breadcrumbNonTextToast(value);
  return getApiErrorMessage(value, fallback);
}

/**
 * Variante para los toasts que NO son de error (success / info / warning /
 * loading).
 *
 * `coerceToastMessage` existe para dos cosas distintas, y solo UNA aplica aqui:
 *
 *   1. que un OBJETO nunca llegue a sonner — es el crash de MB-APP-EXPRESS-1P,
 *      y vale para todos los tipos de toast;
 *   2. que no se le filtre al usuario texto del BACKEND (JSON crudo, ingles,
 *      interpolaciones rotas). Eso solo tiene sentido en un error, porque es el
 *      unico cuyo contenido viene del servidor.
 *
 * Aplicar (2) a un exito convertia el acierto en un fallo: el texto de un
 * success lo escribe la app, y `sanitizeForUser` rechaza cualquier cadena que
 * contenga `undefined`, asi que
 * `toast.success(`${file.name} archivo subido exitosamente`)` con `file.name`
 * indefinido terminaba renderizando "Algo salio mal, intentalo de nuevo." en un
 * toast VERDE. Hay cuatro call sites interpolados asi en el repo.
 *
 * Aqui se conserva (1) intacta y se cambia (2) por el unico limite que si
 * importa en texto propio: el de longitud.
 */
export function coerceToastText(value: unknown): unknown {
  if (typeof value === "string") {
    const text = value.trim();
    if (!text) return value;
    return text.length <= MAX_LENGTH
      ? value
      : `${text.slice(0, MAX_LENGTH - 1).trimEnd()}\u2026`;
  }
  if (isReactElement(value)) return value;
  // Un objeto en un toast de exito sigue siendo un bug: se deja la migaja y se
  // degrada al mensaje generico, que aqui si es la lectura correcta.
  breadcrumbNonTextToast(value);
  return getApiErrorMessage(value, DEFAULT_API_ERROR_MESSAGE);
}
